import React, { useRef, useCallback, useMemo } from 'react'

import { useTranslation } from 'next-i18next'

import { useAppState } from 'context/AppContext'
import { useDeviceType } from 'hooks/useDeviceType'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

import AppQr from './AppQr'
import { ManageMyBookingWingieData } from './data'
import * as S from './ManageMyBookingWingie.styled'

import { resolveWingieCardHref } from 'utils/wingieApiLinks'

const BookingCardItem = ({ item, onCardClick }) => {
  const [show, setShow] = React.useState(false)

  const { isMobile } = useDeviceType()

  const cardHref = item.href || resolveWingieCardHref(null, undefined, item.src?.startsWith('#') ? undefined : item.src)

  const handleClick = () => {
    setShow(true)
    if (onCardClick) {
      onCardClick(item)
    }
  }

  return (
    <>
      <S.ManageMyBookingWingieCardElement
        as={item.isLink ? 'a' : 'div'}
        href={item.isLink ? cardHref : undefined}
        onClick={handleClick}
        target="_blank"
        cursor={item.cursor}
        data-testid={`wingie-homepage-manageMyBooking-${item.type}-card-container`}
      >
        <S.ManageMyBookingWingieCard key={item.heading} isMobile={isMobile} order={item.order} data-testid={`wingie-homepage-manageMyBooking-${item.type}`}>
          <S.Image>{item.isImage ? <img src={item.image} width={60} height={60} alt={item.alt} /> : item.icon}</S.Image>
          <div>
            <S.ManageMyBookingWingieCardHeading data-testid={`wingie-homepage-manageMyBooking-${item.type}-heading`}>{item.heading}</S.ManageMyBookingWingieCardHeading>
            <S.ManageMyBookingWingieCardSubheading data-testid={`wingie-homepage-manageMyBooking-${item.type}-subheading`}>{item.subheading}</S.ManageMyBookingWingieCardSubheading>
          </div>
        </S.ManageMyBookingWingieCard>
      </S.ManageMyBookingWingieCardElement>
      {item.Modal && <item.Modal show={show} setShow={setShow} />}
    </>
  )
}

const ManageMyBookingWingie = () => {
  const { t } = useTranslation('common')

  const { isMobile, isTablet } = useDeviceType()

  const { language, market, host, headerLinks } = useAppState()

  const manageMyBookingContainerRef = useRef<HTMLDivElement>(null)

  const data = useMemo(() => {
    const base = ManageMyBookingWingieData[language ?? 'en'] || ManageMyBookingWingieData.en

    return base.map((item) => {
      if (item.type === 'onlineCheckIn' && headerLinks?.homePageLinks?.checkIn) {
        return { ...item, href: headerLinks.homePageLinks.checkIn, isLink: true }
      }
      if (item.isLink && item.src) {
        return { ...item, href: resolveWingieCardHref(null, host, item.src) }
      }
      return item
    })
  }, [language, headerLinks, host])

  const isWingie = market !== 'tr'

  // ManageMyBookingWingie card click tracking
  const handleCardClick = useCallback(
    (item) => {
      const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        type: 'click',
        message: 'manage_my_booking_card_click',
        environment: environment,
        requestId,
        linkUrl: item.href || (item.src ? resolveWingieCardHref(null, host, item.src) : null),
        cardHeading: item.heading || null,
        cardSubheading: item.subheading || null,
        cardType: item.type || null,
        cardOrder: item.order || null,
        isDialogOpened: !!item.Modal,
        isMobile: !!(isMobile || isTablet),
        market: market,
      })
    },
    [isMobile, isTablet, market, isWingie, host]
  )

  return (
    <div ref={manageMyBookingContainerRef}>
      <S.ManageMyBookingWingieHeading data-testid="wingie-homepage-manageMyBooking-heading">{t('ManageMyBooking.heading')}</S.ManageMyBookingWingieHeading>
      <S.ManageMyBookingRow data-testid="wingie-homepage-manageMyBooking-row" isMobile={isMobile}>
        {data?.map((item) => {
          return (
            <S.ManageMyBookingCol data-testid={`wingie-homepage-manageMyBooking-col-${item.type}`} key={item.type}>
              <BookingCardItem item={item} onCardClick={handleCardClick} />
            </S.ManageMyBookingCol>
          )
        })}
        <S.ManageMyBookingCol data-testid={`wingie-homepage-manageMyBooking-col-${isMobile ? 'App' : 'Qr'}`}>
          <AppQr />
        </S.ManageMyBookingCol>
      </S.ManageMyBookingRow>
    </div>
  )
}

export default ManageMyBookingWingie
